// The player directory, as the app reads it.
//
// P-01 says who exists; this is the shape that crosses the wire when a screen
// asks. League-agnostic on purpose — no roster, no availability, no projection,
// no ownership. Those are league facts and they come from somewhere else, which
// is what lets this one response be computed once per sport and shared by every
// league at no per-league cost.
//
// The query builder lives here rather than in the Worker so it can be tested
// without one. The Worker binds and parses; it decides nothing.

import { z } from 'zod';
import { DirectoryPlayer } from '../providers/directory';
import { decodeCursor, encodeCursor, filterFingerprint, pageOf } from './envelope';

export const DirectoryQuery = z.object({
  sport: z.string().default('football'),
  /** Name prefix or substring. Empty matches everyone. */
  q: z.string().default(''),
  /**
   * Position ids; empty means every position. Capped, because a sport has a
   * handful of positions and an uncapped list walks straight into SQLite's
   * bound-variable limit — which surfaces as a 503 for what is really a bad
   * request, and a wrong failure code is worse than a noisy one.
   */
  positions: z.array(z.string()).max(32).default([]),
  /** A `ProTeam.id`, or `'free-agent'` for nobody's roster. */
  proTeam: z.string().nullable().default(null),
  /**
   * Retired and inactive players are real and must keep their records
   * (invariant 3), but a search box should not lead with them.
   */
  includeInactive: z.boolean().default(false),
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.string().nullable().default(null),
});
export type DirectoryQuery = z.infer<typeof DirectoryQuery>;

/**
 * `Page<DirectoryPlayer>`, plus the one thing a directory has that a page in
 * general does not.
 *
 * `players` became `items` on 2026-09-17 (audit 3.5). It was one of three
 * paging shapes on one surface, and a client that can walk one list should be
 * able to walk all of them. Nothing outside this repository had read it yet.
 */
export const DirectoryPage = pageOf(DirectoryPlayer).extend({
  /** How many match the query, ignoring the page. Always counted here. */
  total: z.number().int().min(0),
  /** When the directory was last confirmed by a source. Drives the stale note. */
  asOf: z.string().nullable(),
});
export type DirectoryPage = z.infer<typeof DirectoryPage>;

export interface SqlQuery {
  sql: string;
  params: (string | number)[];
}

/**
 * A keyset cursor: the last row's sort key. Offsets drift when rows are
 * inserted between pages, and an ingest run inserts constantly.
 *
 * The key is `lastName|id`. It used to cross the wire in exactly that form;
 * since 2026-09-17 it travels inside an opaque cursor with a fingerprint of
 * the filter beside it, so a cursor from one query cannot silently page
 * another (`envelope.ts`).
 */
const KEY = /^(.+)\|(.+)$/;

/**
 * What makes two directory queries the same walk.
 *
 * Everything that changes WHICH rows match, and nothing that changes how many
 * come back at a time: `limit` is absent on purpose, so a client may ask for a
 * bigger page mid-walk without being told to start over. `q` and `positions`
 * are normalized the same way the SQL normalizes them, or the same filter
 * typed two ways would mint two incompatible cursors.
 */
export function directoryFingerprint(query: DirectoryQuery): string {
  return filterFingerprint([
    query.sport,
    query.q.trim().toLowerCase(),
    [...query.positions].sort().join(','),
    query.proTeam,
    query.includeInactive,
  ]);
}

/**
 * The sort key a cursor names, or why this cursor cannot be used here.
 *
 * Separated from the query builder so the endpoint can answer `invalid` with
 * a sentence about what happened, rather than catching an exception and
 * guessing at which of the two mistakes it was.
 */
export function directoryCursorKey(
  query: DirectoryQuery,
): { ok: true; key: string | null } | { ok: false; reason: 'malformed' | 'stale' } {
  if (query.cursor === null) return { ok: true, key: null };
  const decoded = decodeCursor(query.cursor, directoryFingerprint(query));
  if (!decoded.ok) return decoded;
  if (!KEY.test(decoded.key)) return { ok: false, reason: 'malformed' };
  return { ok: true, key: decoded.key };
}

function where(query: DirectoryQuery): { clause: string; params: (string | number)[] } {
  const parts = ['sport = ?'];
  const params: (string | number)[] = [query.sport];

  if (!query.includeInactive) {
    parts.push("status IN ('active', 'practice-squad')");
  }
  if (query.q.trim().length > 0) {
    // Match either half of the name as well as the whole, so "mahomes" and
    // "pat mah" both find him.
    parts.push('(lower(display_name) LIKE ? OR lower(last_name) LIKE ?)');
    const like = `%${query.q.trim().toLowerCase()}%`;
    params.push(like, like);
  }
  if (query.positions.length > 0) {
    // positions_json is a JSON array; EXISTS over json_each is the index-free
    // but correct way to ask "does this player play any of these?".
    parts.push(
      `EXISTS (SELECT 1 FROM json_each(players.positions_json) WHERE json_each.value IN (${query.positions
        .map(() => '?')
        .join(', ')}))`,
    );
    params.push(...query.positions);
  }
  if (query.proTeam === 'free-agent') {
    parts.push('pro_team IS NULL');
  } else if (query.proTeam !== null) {
    parts.push('pro_team = ?');
    params.push(query.proTeam);
  }
  return { clause: parts.join(' AND '), params };
}

// `as_of` is when a source last confirmed the record (ICD-P01): the sport's
// last directory rebuild, which confirms every row it computes, or the row's
// own last change if that is later. A rebuild writes only the rows that changed
// (DATA_LAYER_DESIGN Phase 0), so `updated_at` alone is when it last changed.
const COLUMNS = `id, sport, first_name, last_name, display_name, short_name,
                 positions_json, pro_team, status, injury_status, injury_note, injury_read_at,
                 experience_years, depth_position, depth_rank, external_ids_json,
                 MAX(updated_at, COALESCE((SELECT b.built_at FROM directory_builds b
                                            WHERE b.sport = players.sport), updated_at)) AS as_of`;

/**
 * The page itself. Ordered by name so paging is stable and readable.
 *
 * `after` is the decoded sort key from `directoryCursorKey`, never the cursor
 * as it arrived: validating and using are two steps, and a builder that also
 * validated would have to throw to refuse.
 */
export function directoryPageQuery(query: DirectoryQuery, after: string | null = null): SqlQuery {
  const { clause, params } = where(query);
  const parts = [clause];
  if (after !== null) {
    const m = KEY.exec(after);
    if (!m) throw new Error('malformed cursor key');
    // The leading `last_name >= ?` is the range `players_sport_name` is
    // searched on. The OR alone, with its own placeholders, cannot be one, and
    // every page after the first read the sport's directory from the top.
    parts.push('last_name >= ? AND (last_name > ? OR (last_name = ? AND id > ?))');
    params.push(m[1]!, m[1]!, m[1]!, m[2]!);
  }
  return {
    sql: `SELECT ${COLUMNS} FROM players WHERE ${parts.join(' AND ')}
          ORDER BY last_name, id LIMIT ?`,
    // One extra row is how we know whether there is a next page without a
    // second count query.
    params: [...params, query.limit + 1],
  };
}

export function directoryCountQuery(query: DirectoryQuery): SqlQuery {
  const { clause, params } = where(query);
  return { sql: `SELECT COUNT(*) AS n FROM players WHERE ${clause}`, params };
}

// ── The same walk over a list in memory ──────────────────────────────────────
//
// THE DIRECTORY ARTIFACT'S (DATA_LAYER_DESIGN §4.4, ICD-S10): every player of
// the sport in one document, built after each directory rebuild, so a page is
// a filter over a list the Worker already holds rather than a scan of the
// table and a count of it on every page (6,086 rows read to count, on the dev
// database, 2026-10-08). It answers exactly what the two queries above answer
// over the same rows, which `ingest:verify` holds over a matrix of queries,
// and so it reproduces three things of SQLite's rather than JavaScript's:
//
//   lower()   folds A to Z and nothing else (no ICU in D1)
//   LIKE      `%` is any run and `_` any one character, wherever they appear
//             in what was typed, ASCII letters matched without case
//   BINARY    the order of the UTF-8 bytes, which is code point order; a
//             JavaScript `<` compares UTF-16 units, which differs past U+FFFF

/** SQLite's `lower()` without ICU: A to Z, and nothing else. */
export function sqliteLower(s: string): string {
  return s.replace(/[A-Z]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 32));
}

/** `text LIKE pattern` as SQLite answers it. */
export function sqliteLike(text: string, pattern: string): boolean {
  let source = '';
  for (const ch of sqliteLower(pattern)) {
    if (ch === '%') source += '[\\s\\S]*';
    else if (ch === '_') source += '[\\s\\S]';
    // Only the syntax characters: under the `u` flag any other escape is an error.
    else source += ch.replace(/[\^$\\.*+?()[\]{}|/]/g, '\\$&');
  }
  // The `u` flag, so `_` is one character and not half of one. Case is
  // handled by lowering both sides' ASCII, which is all SQLite folds.
  return new RegExp(`^${source}$`, 'u').test(sqliteLower(text));
}

const SURROGATE = /[\uD800-\uDFFF]/;

/** SQLite's BINARY collation: code point order. */
export function binaryCompare(a: string, b: string): number {
  if (!SURROGATE.test(a) && !SURROGATE.test(b)) return a < b ? -1 : a > b ? 1 : 0;
  const x = [...a].map((c) => c.codePointAt(0)!);
  const y = [...b].map((c) => c.codePointAt(0)!);
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    if (x[i] !== y[i]) return x[i]! < y[i]! ? -1 : 1;
  }
  return x.length - y.length;
}

/** The directory's order, `ORDER BY last_name, id`, for a list a page is cut from. */
export const compareDirectory = (
  a: Pick<DirectoryPlayer, 'lastName' | 'id'>,
  b: Pick<DirectoryPlayer, 'lastName' | 'id'>,
): number => binaryCompare(a.lastName, b.lastName) || binaryCompare(a.id, b.id);

/** Whether a player is in the query's filter: `where()`, in memory. */
export function directoryMatches(query: DirectoryQuery, p: DirectoryPlayer): boolean {
  if (p.sport !== query.sport) return false;
  if (!query.includeInactive && p.status !== 'active' && p.status !== 'practice-squad')
    return false;
  const q = query.q.trim().toLowerCase();
  if (q.length > 0) {
    const like = `%${q}%`;
    if (!sqliteLike(p.displayName, like) && !sqliteLike(p.lastName, like)) return false;
  }
  if (query.positions.length > 0 && !p.positions.some((pos) => query.positions.includes(pos)))
    return false;
  if (query.proTeam === 'free-agent') return p.proTeam === null;
  if (query.proTeam !== null) return p.proTeam === query.proTeam;
  return true;
}

/**
 * A page and its count from a list in `compareDirectory` order: what
 * `directoryPageQuery` and `directoryCountQuery` answer together. `after` is
 * the decoded key, as there.
 */
export function directoryPageOf<P extends DirectoryPlayer>(
  query: DirectoryQuery,
  players: readonly P[],
  after: string | null = null,
): { items: P[]; hasMore: boolean; total: number } {
  let key: { lastName: string; id: string } | null = null;
  if (after !== null) {
    const m = KEY.exec(after);
    if (!m) throw new Error('malformed cursor key');
    key = { lastName: m[1]!, id: m[2]! };
  }
  const items: P[] = [];
  let total = 0;
  let hasMore = false;
  for (const p of players) {
    if (!directoryMatches(query, p)) continue;
    total += 1;
    if (key !== null && compareDirectory(p, key) <= 0) continue;
    if (items.length < query.limit) items.push(p);
    else hasMore = true;
  }
  return { items, hasMore, total };
}

/** The raw sort key for a row. Internal to the walk; never the wire form. */
export const keyFor = (p: DirectoryPlayer): string => `${p.lastName}|${p.id}`;

/** The wire cursor for "everything after this row, under this same query". */
export const cursorFor = (p: DirectoryPlayer, query: DirectoryQuery): string =>
  encodeCursor(directoryFingerprint(query), keyFor(p));

/** A D1 row back into the domain shape. The one place column names are known. */
export function rowToDirectoryPlayer(r: Record<string, unknown>): DirectoryPlayer {
  return DirectoryPlayer.parse({
    id: r['id'],
    sport: r['sport'],
    firstName: r['first_name'],
    lastName: r['last_name'],
    displayName: r['display_name'],
    shortName: r['short_name'] ?? null,
    positions: JSON.parse(String(r['positions_json'])),
    proTeam: r['pro_team'] ?? null,
    status: r['status'],
    injury: r['injury_status']
      ? {
          status: r['injury_status'],
          note: r['injury_note'] ?? null,
          readAt: r['injury_read_at'] ?? null,
        }
      : null,
    experienceYears: Number(r['experience_years'] ?? 0),
    depth:
      r['depth_rank'] == null || r['depth_position'] == null
        ? null
        : { position: String(r['depth_position']), rank: Number(r['depth_rank']) },
    externalIds: JSON.parse(String(r['external_ids_json'] ?? '{}')),
    asOf: r['as_of'],
  });
}
